import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useNotifications, useMarkAsRead } from '@/hooks/use-notifications';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

export function PopupNotificationManager() {
    const { data: notifications = [] } = useNotifications(true, 50); // Fetch unread only
    const markAsRead = useMarkAsRead();

    const [currentPopupId, setCurrentPopupId] = useState<string | null>(null);

    // Find the first unread popup notification
    const popupNotifications = notifications.filter(n => n.is_popup);
    const activePopup = popupNotifications.find(n => n.id === currentPopupId) || popupNotifications[0];

    useEffect(() => {
        if (activePopup && activePopup.id !== currentPopupId) {
            setCurrentPopupId(activePopup.id);
        } else if (!activePopup && currentPopupId) {
            setCurrentPopupId(null);
        }
    }, [activePopup, currentPopupId]);

    const handleAcknowledge = () => {
        if (activePopup) {
            markAsRead.mutate(activePopup.id);
            setCurrentPopupId(null);
        }
    };

    if (!activePopup) {
        return null;
    }

    return (
        <Dialog open={!!activePopup} onOpenChange={(open) => {
            // Prevent closing by clicking outside if it's a mandatory popup
            if (!open) {
                handleAcknowledge();
            }
        }}>
            <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
                <div className="p-6">
                    <DialogHeader className="mb-4">
                        <DialogTitle className="text-2xl">{activePopup.title}</DialogTitle>
                        {!activePopup.popup_content && (
                            <DialogDescription className="text-base mt-2">
                                {activePopup.message}
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    {activePopup.popup_content && (
                        <ScrollArea className="max-h-[60vh] -mx-6 px-6">
                            <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none pb-6">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                >
                                    {activePopup.popup_content}
                                </ReactMarkdown>
                            </div>
                        </ScrollArea>
                    )}

                    <DialogFooter className="mt-6 border-t pt-4">
                        <Button onClick={handleAcknowledge} className="w-full sm:w-auto">
                            Got it
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
